import { usePrivy } from '@privy-io/react-auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { createStakeString } from '@/common/utils/utils';
import { TetherIconColorOptions } from '@/components/CurrencySymbolMask/CurrencyIcon';
import TableStakeInfo from '@/components/TableStakeInfo';
import { useToast } from '@/components/toast/useToast';
import { useJoinTableBatchSession } from '@/features/account-abstraction/hooks/useJoinTableBatchSession';
import { useRebuyBatchSession } from '@/features/account-abstraction/hooks/useRebuyBatchSession';
import { useSessionKeyAndStorageClient } from '@/features/account-abstraction/hooks/useSessionKeyAndStorageClient';
import { useSmartAccountInitialization } from '@/features/account-abstraction/hooks/useSmartAccountInitialization';
import TablePlay from '@/features/poker-table/TablePlay';
import useAutoTopUp from '@/features/poker-table/hooks/useAutoTopUp';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import useWindowFocus from '@/hooks/useWindowFocus';
import TableModal from '@/pages/table/components/TableModal/TableModal';
import { chatConnectAction } from '@/services/chat/chatActions';
import { ReducerState } from '@/services/common/lib/reducerState';
import { PokerCategory } from '@/services/common/types/pokerCategory';
import { LobbyTableOpenBuyInAction } from '@/services/lobby/lobbyActions';
import { setWaitForBBAction, tableConnectAction, tableInit } from '@/services/table/tableActions';
import { sitInRest } from '@/services/table/tableService';

import useIsMobileView from '../../hooks/useIsMobileView';
import styles from './Table.module.css';
import ActionButton, { ActionButtonVariant } from './components/ActionButton/ActionButton';
import SitBackIn from './components/SitBackIn/SitBackIn';
import TableHeader from './components/TableHeader/TableHeader';
import TableSounds from './components/TableSounds/TableSounds';
import Concierge from './play/elements/Concierge';
import TableStreams, { StreamType } from './play/elements/TableStreams';
import UserActions from './play/elements/UserActions';
import DoubleLoginModal from '@/components/DoubleLoginModal/DoubleLoginModal';

const Table = () => {
  const { getAccessToken } = usePrivy();
  const { tableId } = useParams();

  useAutoTopUp();

  if (!tableId) {
    throw new Error('No tableId');
  }

  const user = useSelector((state: ReducerState) => state.auth);
  const userId: string = user.id as string;

  if (!userId) {
    throw new Error('No userId');
  }

  useSmartAccountInitialization();
  useSessionKeyAndStorageClient();
  useJoinTableBatchSession();
  useRebuyBatchSession();

  const { toast } = useToast();

  const isMobileView = useIsMobileView({ checkForUserAgent: true });
  const [showChat, setShowChat] = useState<boolean>(!isMobileView);
  const [showTable, setShowTable] = useState<boolean>(true);
  const [streamType, setStreamType] = useState<StreamType>(StreamType.CHAT);

  const isFocused = useWindowFocus();

  const prevIsMobileViewRef = useRef(isMobileView);
  const dispatch = useDispatch();

  const tableState = useSelector((state: ReducerState) => state.tables?.[tableId]);

  useEffect(() => {
    // check if the user is still authenticated when the window is focused
    if (isFocused) {
      getAccessToken().then((token: string | null) => {
        if (!token) {
          window.location.reload();
        }
      });
    }
  }, [isFocused, getAccessToken]);

  useEffect(() => {
    if (!window.location.hash && window.addEventListener) {
      window.addEventListener('load', function () {
        setTimeout(function () {
          window.scrollTo(0, 0);
        }, 0);
      });
      window.addEventListener('orientationchange', function () {
        setTimeout(function () {
          window.scrollTo(0, 0);
        }, 0);
      });
      window.addEventListener('touchstart', function () {
        setTimeout(function () {
          window.scrollTo(0, 0);
        }, 0);
      });
    }
  }, []);

  const handleBuyIn = useCallback(
    (tableId: string, seat: number, stack: number) => {
      dispatch(LobbyTableOpenBuyInAction(tableId, seat, stack));
    },
    [dispatch],
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const seat = searchParams.get('seat');
    const stack = searchParams.get('stack');

    if (tableId && seat && stack) {
      handleBuyIn(tableId, parseInt(seat), parseInt(stack));
    }
  }, [tableId, handleBuyIn]);

  useEffect(() => {
    const prevIsMobileView = prevIsMobileViewRef.current;

    if (isMobileView !== prevIsMobileView) {
      setShowChat(!isMobileView);
    }

    prevIsMobileViewRef.current = isMobileView;
  }, [isMobileView]);

  const tableTitle = useMemo(() => {
    if (tableState?.name && tableState?.game) {
      return `${tableState.name} - ${tableState.game} ${createStakeString(tableState)}`;
    }
    return undefined;
  }, [tableState?.name, tableState?.game, tableState?.smallBlind, tableState?.bigBlind]);

  useDocumentTitle(tableTitle);

  const [waitingForBigBlind, setWaitingForBigBlind] = useState<boolean | undefined>(tableState?.playerSeat?.waiting);

  useEffect(() => {
    setWaitingForBigBlind(tableState?.playerSeat?.waiting);
  }, [tableState?.playerSeat?.waiting]);

  useEffect(() => {
    if (tableId && !tableState?.url) {
      dispatch(tableInit(tableId));
    }
  }, [dispatch, tableId, tableState?.url]);

  useEffect(() => {
    const tableUrl = tableState?.url;
    if (tableId && tableUrl && userId) {
      dispatch(tableConnectAction(tableId, tableUrl));
      dispatch(chatConnectAction(tableId, tableUrl));
    }
  }, [dispatch, tableId, tableState?.url, userId]);

  useEffect(() => {
    setShowTable(!(isMobileView && showChat));
  }, [isMobileView, showChat]);

  // useEffect(() => {

  //   const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  //     dispatch(leaveTableAttemptAction(tableId));
  //     event.preventDefault(); // Cancel the event
  //   };

  //   window.addEventListener("beforeunload", handleBeforeUnload, true);

  //   return () => {
  //     window.removeEventListener("beforeunload", handleBeforeUnload);
  //   };
  // }, [dispatch, tableId]);

  if (!tableId || !tableState) {
    return null;
  }

  const sitIn = async () => {
    try {
      await sitInRest({
        tableId: tableState.tableId,
        tableUrl: tableState.url,
      });
    } catch (_error) {
      toast({ description: 'Error when sitting in' }); // TODO : DISPLAY APPROPRIATE MESSAGE
    }
  };

  const playerSeat = tableState?.playerSeat;
  const isSitout = playerSeat?.active === false;

  const canWaitForBigBlind =
    !isSitout &&
    tableState.dealerSeat >= 0 && // should not be showing this before the game starts
    tableState.maxPlayers !== 2 && // should not be showing this if its a heads up game
    tableState.currentVariant.category !== PokerCategory.STUD &&
    playerSeat?.playerIndex === null &&
    (playerSeat.handsDealt === 0 || playerSeat.skippedSB || playerSeat.skippedBB || playerSeat.newPlayer);

  const playersSitting = tableState?.seats?.filter((s) => !!s).length;

  return (
    <div className={`${styles.container} flex min-h-dvh flex-col`}>
      <TableHeader
        selectedTableId={tableId}
        userId={userId}
        showChat={showChat}
        setShowChat={setShowChat}
        game={tableState.game}
        stakes={<TableStakeInfo fontSize="sm" color={TetherIconColorOptions.white} table={tableState} />}
        playerSeat={playerSeat}
        tableId={tableState.tableId}
        tableTabsInDesktopEnabled={Boolean(user.settings?.tableTabsInDesktopEnabled)}
      />

      <div className={`${styles.tableContainer} flex min-h-0 flex-grow items-center justify-center`}>
        {showTable && <TablePlay tableState={tableState} />}
      </div>

      <div className={`${styles.actionAndChatContainer} flex min-h-0 pt-8`}>
        <div
          className={` ${styles.chatStreamContainer} ${showChat ? styles.show : styles.hide} ${isMobileView && showChat ? styles.mobileActive : ''} `}
        >
          {tableState && (
            <TableStreams tableId={tableId} userId={userId} streamType={streamType} setStreamType={setStreamType} />
          )}
        </div>
        <div className={twMerge(styles.actionContainer)}>
          {/* Always render this container, but conditionally render its contents */}
          {!playerSeat?.leftTable && (
            <>
              {isSitout && !(showChat && isMobileView) && (
                <div className="flex-1">
                  <SitBackIn onClick={sitIn} />
                </div>
              )}

              {canWaitForBigBlind && (
                <ActionButton
                  variant={
                    waitingForBigBlind
                      ? ActionButtonVariant.WaitForBigBlindActive
                      : ActionButtonVariant.WaitForBigBlindInactive
                  }
                  isChecked={!!waitingForBigBlind}
                  onClick={() => {
                    dispatch(setWaitForBBAction(tableId, !waitingForBigBlind));
                    setWaitingForBigBlind((prev) => !prev);
                  }}
                  isPlayerTurn={false}
                  className="!min-w-40"
                  containerClassName="flex justify-center"
                >
                  Wait for Big Blind
                </ActionButton>
              )}
              {!playerSeat && tableState?.url && ((isMobileView && !showChat) || !isMobileView) && (
                <Concierge
                  tableId={tableId}
                  userId={userId}
                  fullTable={playersSitting > 0 && playersSitting === tableState.maxPlayers}
                  waitlist={tableState?.waitlist}
                />
              )}

              {tableState.handState?.state &&
                ((playerSeat?.availableActions && playerSeat?.active) ||
                  (playerSeat?.preActionsAvailable && playerSeat?.active)) && (
                  <UserActions tableId={tableState.tableId} />
                )}
            </>
          )}
        </div>
      </div>
      {user.settings?.soundEnabled && <TableSounds tableState={tableState} />}
      <TableModal />
      <DoubleLoginModal />
    </div>
  );
};

export default Table;
